import { useState, type FormEvent } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Server, Globe, Router, Camera, Monitor, Box, Wifi, Network,
  CheckCircle2, AlertCircle, Loader2, Copy, ArrowRight,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTenantSshKey, useCreateDevice, useTestSsh, useDevices,
  useCreateConnector,
  type DeviceKind, type Device,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

const KIND_OPTIONS: { value: DeviceKind; label: string; description: string; icon: typeof Server }[] = [
  { value: "linux",        label: "Linux server",       description: "Ubuntu / Debian / RHEL / AlmaLinux. Filebeat + SSH.",                icon: Server },
  { value: "cpanel",       label: "cPanel host",        description: "Linux + cPanel-specific log paths shipped to Graylog.",              icon: Globe },
  { value: "mikrotik",     label: "MikroTik router",    description: "NetFlow export to Tyrael; SSH (read-only).",                         icon: Router },
  { value: "hikvision",    label: "Hikvision",          description: "NVR / DVR / MinMoe terminal. HTTP Digest + syslog.",                 icon: Camera },
  { value: "windows",      label: "Windows server",     description: "OpenSSH on Windows. Often the jump host into a customer LAN.",       icon: Monitor },
  { value: "unifi_ap",     label: "UniFi access point", description: "U6 / nanoHD / AC line. SSH via controller-pushed tenant key.",       icon: Wifi },
  { value: "unifi_switch", label: "UniFi switch",       description: "USW-line managed switch. SSH for port + MAC table visibility.",      icon: Network },
  { value: "other",        label: "Other",              description: "Generic — connectors added later by hand.",                          icon: Box },
];

// What surface this kind exposes. Drives which fields and which wizard
// steps are appropriate. Hikvision is the odd one out — pure HTTP + the
// device pushes syslog when we tell it to via ISAPI; no SSH at all.
const KIND_HAS_SSH: Record<DeviceKind, boolean> = {
  linux: true, cpanel: true, mikrotik: true, windows: true, other: true,
  unifi_ap: true, unifi_switch: true,
  hikvision: false,
};
// Whether the wizard's filebeat-install playbook applies. Today only
// Debian/RHEL family hosts get auto-installed; everything else lands as
// a bare device row + the operator adds connectors from the device page.
const KIND_HAS_FILEBEAT_INSTALL: Record<DeviceKind, boolean> = {
  linux: true, cpanel: true,
  mikrotik: false, hikvision: false, windows: false, other: false,
  unifi_ap: false, unifi_switch: false,
};
// Whether the tenant SSH key is already authorised on the device by
// some out-of-band mechanism (so the wizard can skip the "Authorise key"
// step). True for UniFi devices because the controller pushes the
// authorised_keys list to every adopted AP/switch — so the moment the
// public key is added to the controller, all 22 APs accept it.
// Everything else: the wizard shows the install-command + fingerprint
// for the operator to paste on the box.
const KIND_HAS_PREINSTALLED_KEY: Record<DeviceKind, boolean> = {
  unifi_ap: true, unifi_switch: true,
  linux: false, cpanel: false, mikrotik: false, hikvision: false,
  windows: false, other: false,
};

type Step = "kind" | "details" | "reach" | "key" | "test" | "provision" | "done";

interface ProvisionEvent {
  step?: string;
  ok?: boolean | null;
  detail?: string;
  done?: boolean;
  status?: string;
  last_error?: string | null;
}

export function AddDeviceWizard({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [step, setStep]         = useState<Step>("kind");
  const [kind, setKind]         = useState<DeviceKind>("linux");
  const [name, setName]         = useState("");
  const [hostname, setHostname] = useState("");
  const [sshPort, setSshPort]   = useState(22);
  const [sshUser, setSshUser]   = useState("root");
  const [role, setRole]         = useState("");
  // Reachability — null means direct SSH, otherwise the id of an
  // already-declared device in the same tenant to ProxyJump through.
  // Server-side trigger enforces same-tenant + single-hop.
  const [proxyJumpDeviceId, setProxyJumpDeviceId] = useState<number | null>(null);
  const [device, setDevice]     = useState<Device | null>(null);
  const [provisionEvents, setProvisionEvents] = useState<ProvisionEvent[]>([]);
  const [provisionDone, setProvisionDone] = useState<ProvisionEvent | null>(null);

  const sshKey = useTenantSshKey(open && step === "key");
  const createDevice = useCreateDevice();
  const testSsh = useTestSsh();
  const createConnector = useCreateConnector();
  const allDevices = useDevices();
  const qc = useQueryClient();

  // Valid jump-host candidates = active devices in the tenant that are
  // NOT themselves proxy-jumped (schema only allows single-hop). We
  // filter client-side; the list endpoint already returns everything the
  // tenant can see. The UX dropdown only renders this filtered set.
  const jumpCandidates: Device[] = (allDevices.data?.items ?? []).filter(
    (d) => d.is_active && d.proxy_jump_device_id === null,
  );
  const jumpHost = proxyJumpDeviceId !== null
    ? jumpCandidates.find((d) => d.id === proxyJumpDeviceId) ?? null
    : null;

  function reset() {
    setStep("kind");
    setKind("linux");
    setName(""); setHostname(""); setSshPort(22); setSshUser("root"); setRole("");
    setProxyJumpDeviceId(null);
    setDevice(null);
    setProvisionEvents([]); setProvisionDone(null);
    testSsh.reset(); createDevice.reset();
  }

  async function onCreateAndAdvance(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !hostname.trim()) return;

    // For non-SSH kinds (hikvision today) there's no test step that
    // would reject bad inputs later, so we go ahead and create the row
    // here as the user's only confirmation. proxy_jump_device_id is
    // null because non-SSH kinds don't use the proxy-jump primitive.
    if (!KIND_HAS_SSH[kind]) {
      try {
        const d = await createDevice.mutateAsync({
          name: name.trim(),
          hostname: hostname.trim(),
          ssh_port: sshPort,
          ssh_user: sshUser.trim(),
          kind,
          role: role.trim() || null,
          proxy_jump_device_id: null,
        });
        setDevice(d);
        setStep("done");
      } catch { /* error surfaced inline by createDevice.isError */ }
      return;
    }

    // SSH-shellable kinds: DEFER device creation until after the test
    // passes. Creating early caused two real bugs:
    //   - orphan rows whenever the test step failed mid-flow,
    //   - proxy_jump_device_id always persisting as null because the
    //     user picks it AFTER this submit fires.
    // Now the device only lands in the DB once we've proven the SSH
    // path works and we know the final proxy-jump choice.
    setStep("reach");
  }

  // Called when the user is done picking reachability — moves to either
  // the manual-install-key step OR straight to test, depending on whether
  // the kind already has the tenant key (UniFi: yes, via controller push).
  function onReachContinue() {
    setStep(KIND_HAS_PREINSTALLED_KEY[kind] ? "test" : "key");
  }

  async function runTestSsh() {
    if (!hostname.trim()) return;
    const result = await testSsh.mutateAsync({
      hostname: hostname.trim(),
      ssh_port: sshPort,
      ssh_user: sshUser.trim(),
      // Sending null is fine — the server only uses non-null values.
      proxy_jump_device_id: proxyJumpDeviceId,
    });
    if (!result.ok) return;

    // Test passed → NOW persist the device. This is the first time
    // the device row hits the DB; if the test had failed we'd just
    // leave the user on this step with no orphan to clean up.
    // proxy_jump_device_id is captured here so the reach-step
    // selection actually makes it onto the row.
    let createdDevice: Device;
    try {
      createdDevice = await createDevice.mutateAsync({
        name: name.trim(),
        hostname: hostname.trim(),
        ssh_port: sshPort,
        ssh_user: sshUser.trim(),
        kind,
        role: role.trim() || null,
        proxy_jump_device_id: proxyJumpDeviceId,
      });
      setDevice(createdDevice);
    } catch (e) {
      // The createDevice error is rendered by createDevice.isError in
      // the test-step JSX so the user sees what went wrong (most
      // commonly: name already taken).
      console.error("wizard: create device after test passed:", e);
      return;
    }

    // For kinds that go through the provision playbook (linux/cpanel)
    // the playbook creates connectors itself, so skip the auto-create
    // here. For all other SSH-shellable kinds (mikrotik / windows /
    // unifi_ap / unifi_switch / other) auto-create the SSH connector
    // now — otherwise the user lands on Done with a verified SSH path
    // but no canonical connector row, which makes the wizard feel
    // like it forgot to do its job. The verifier runs server-side on
    // create; we don't need to check it again here because we just
    // proved the same SSH path works above.
    if (!KIND_HAS_FILEBEAT_INSTALL[kind]) {
      try {
        await createConnector.mutateAsync({
          deviceId: createdDevice.id,
          body: { kind: "ssh", config: {} },
        });
      } catch (e) {
        // Non-blocking — device + SSH path are good, the user can add
        // the connector manually from the device detail page. The
        // Done panel surfaces this case with an amber heads-up.
        console.error("wizard: auto-create SSH connector failed:", e);
      }
    }
    setStep(KIND_HAS_FILEBEAT_INSTALL[kind] ? "provision" : "done");
  }

  async function startProvisioning() {
    if (!device) return;
    setProvisionEvents([]);
    setProvisionDone(null);

    // POST + read NDJSON stream.
    const r = await fetch(`/api/devices/${device.id}/provision`, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/x-ndjson" },
    });
    if (!r.ok || !r.body) {
      setProvisionEvents([{ step: "error", ok: false, detail: `HTTP ${r.status}: ${await r.text()}` }]);
      return;
    }

    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let buffered = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });
      const lines = buffered.split("\n");
      buffered = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const ev = JSON.parse(line) as ProvisionEvent;
          if (ev.done) setProvisionDone(ev);
          else setProvisionEvents((prev) => [...prev, ev]);
        } catch { /* ignore parse errors on partial lines */ }
      }
    }
    qc.invalidateQueries({ queryKey: ["devices"] });
    setStep("done");
  }

  function close() {
    reset();
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <SheetContent className="sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle>Add device</SheetTitle>
          <SheetDescription>
            <StepBadge current={step} />
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "kind" && (
            <div className="space-y-3">
              <div className="text-sm font-medium">What kind of device is this?</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {KIND_OPTIONS.map(({ value, label, description, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => { setKind(value); setStep("details"); }}
                    className={cn(
                      "flex items-start gap-3 rounded-md border p-3 text-left transition-colors",
                      "border-border bg-background hover:border-primary hover:bg-primary/5",
                    )}
                  >
                    <Icon className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="text-sm font-medium">{label}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "details" && (
            <form onSubmit={onCreateAndAdvance} className="space-y-3">
              <div className="text-sm font-medium">Device details</div>
              <FieldRow
                label="Name *"
                hint={kind === "hikvision"
                  ? "What this device is called in Tyrael. Will also be the Graylog `source:` filter for its syslog."
                  : "What this device is called in Tyrael (must be unique)."}
              >
                <input
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={kind === "hikvision" ? "nvr-cambridge-01" : "cham.nws.ro"}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </FieldRow>
              <FieldRow
                label="Hostname / IP *"
                hint={kind === "hikvision"
                  ? "Where Tyrael will reach the device's HTTP admin (ISAPI). Just the host — port goes in the http_digest connector URL later."
                  : "What Tyrael will SSH into."}
              >
                <input
                  value={hostname} onChange={(e) => setHostname(e.target.value)}
                  placeholder={kind === "hikvision" ? "5.2.225.55" : "cham.nws.ro"}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </FieldRow>
              {KIND_HAS_SSH[kind] && (
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="SSH port"><input type="number" value={sshPort} onChange={(e) => setSshPort(parseInt(e.target.value || "22"))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></FieldRow>
                  <FieldRow label="SSH user"><input value={sshUser} onChange={(e) => setSshUser(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></FieldRow>
                </div>
              )}
              <FieldRow label="Role (optional)" hint="Free text, e.g. 'mail server' or 'edge router'."><input value={role} onChange={(e) => setRole(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></FieldRow>
              {createDevice.isError && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600">
                  Couldn't create device. The name might be taken.
                </div>
              )}
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep("kind")} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
                <button type="submit" disabled={createDevice.isPending} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                  {createDevice.isPending ? "Saving…" : "Continue →"}
                </button>
              </div>
            </form>
          )}

          {step === "reach" && (
            <div className="space-y-4">
              <div className="text-sm font-medium">Reachability</div>
              <div className="text-xs text-muted-foreground">
                How does Tyrael reach this device? Direct if it has a public IP or a port-forward to it. Via-jump if it sits on a private LAN behind another device Tyrael already has SSH to.
              </div>

              <div className="space-y-2">
                <label className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                  proxyJumpDeviceId === null
                    ? "border-primary bg-primary/5"
                    : "border-border bg-background hover:border-primary/50",
                )}>
                  <input
                    type="radio" name="reach" value="direct"
                    checked={proxyJumpDeviceId === null}
                    onChange={() => setProxyJumpDeviceId(null)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium">Direct</div>
                    <div className="text-xs text-muted-foreground">
                      Tyrael connects straight to <code className="font-mono">{sshUser}@{hostname}:{sshPort}</code>.
                    </div>
                  </div>
                </label>

                <label className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                  proxyJumpDeviceId !== null
                    ? "border-primary bg-primary/5"
                    : "border-border bg-background hover:border-primary/50",
                  jumpCandidates.length === 0 && "cursor-not-allowed opacity-60",
                )}>
                  <input
                    type="radio" name="reach" value="proxy"
                    checked={proxyJumpDeviceId !== null}
                    onChange={() => {
                      if (jumpCandidates.length > 0 && proxyJumpDeviceId === null) {
                        // Default to the first candidate so the dropdown has a value
                        setProxyJumpDeviceId(jumpCandidates[0].id);
                      }
                    }}
                    disabled={jumpCandidates.length === 0}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Via another device (SSH ProxyJump)</div>
                    <div className="text-xs text-muted-foreground">
                      {jumpCandidates.length === 0
                        ? "No suitable jump hosts in your tenant yet. Add one with Direct first (e.g. a Windows box with port-forwarded SSH, or a MikroTik gateway)."
                        : "Tyrael opens SSH to a jump host you already declared, then tunnels to this device. The schema only allows single-hop chains."}
                    </div>
                    {proxyJumpDeviceId !== null && jumpCandidates.length > 0 && (
                      <select
                        value={proxyJumpDeviceId}
                        onChange={(e) => setProxyJumpDeviceId(parseInt(e.target.value))}
                        className="mt-2 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                      >
                        {jumpCandidates.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} ({d.hostname}:{d.ssh_port}, kind={d.kind})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>
              </div>

              {proxyJumpDeviceId !== null && jumpHost && (
                <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    Path preview
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 font-mono">
                    <span>Tyrael</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span>{jumpHost.ssh_user}@{jumpHost.hostname}:{jumpHost.ssh_port}</span>
                    <span className="text-muted-foreground">({jumpHost.name})</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span>{sshUser}@{hostname}:{sshPort}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep("details")} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
                <button
                  type="button"
                  onClick={onReachContinue}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === "key" && (
            <div className="space-y-4">
              <div className="text-sm font-medium">Authorize Tyrael's SSH key on this device</div>
              <div className="text-xs text-muted-foreground">
                Run this on <code className="rounded bg-muted px-1">{hostname}</code> as root (or as <code className="rounded bg-muted px-1">{sshUser}</code> if that's a user with passwordless sudo). Tyrael will use this key to install the agent and read logs going forward.
              </div>
              <div className="rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
                {sshKey.isLoading && "Loading public key…"}
                {sshKey.data && (
                  <>
                    <div className="break-all whitespace-pre-wrap">{sshKey.data.public_key}</div>
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">Fingerprint</div>
                    <div className="text-[11px]">{sshKey.data.fingerprint}</div>
                  </>
                )}
              </div>
              {sshKey.data && (
                <button
                  type="button"
                  onClick={() => {
                    const cmd = `mkdir -p ~/.ssh && chmod 700 ~/.ssh && echo '${sshKey.data!.public_key}' >> ~/.ssh/authorized_keys && chmod 600 ~/.ssh/authorized_keys`;
                    navigator.clipboard.writeText(cmd);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
                >
                  <Copy className="h-3 w-3" /> Copy install command
                </button>
              )}
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep("reach")} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
                <button type="button" onClick={() => setStep("test")} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                  Test connection →
                </button>
              </div>
            </div>
          )}

          {step === "test" && (
            <div className="space-y-3">
              <div className="text-sm font-medium">Verify SSH connection</div>
              {jumpHost ? (
                <div className="text-xs text-muted-foreground">
                  Tyrael will open the two-hop path:{" "}
                  <code className="rounded bg-muted px-1">
                    Tyrael → {jumpHost.ssh_user}@{jumpHost.hostname}:{jumpHost.ssh_port} → {sshUser}@{hostname}:{sshPort}
                  </code>{" "}
                  and run <code className="rounded bg-muted px-1">whoami</code> on the target.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Tyrael will connect to <code className="rounded bg-muted px-1">{sshUser}@{hostname}:{sshPort}</code> and run <code className="rounded bg-muted px-1">whoami</code>.
                </div>
              )}
              <button
                type="button"
                onClick={runTestSsh}
                disabled={testSsh.isPending || createDevice.isPending || createConnector.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {testSsh.isPending      ? "Connecting…"
                 : createDevice.isPending    ? "Saving device…"
                 : createConnector.isPending ? "Creating SSH connector…"
                 : "Run test"}
              </button>
              {testSsh.data && (
                testSsh.data.ok ? (
                  <div className="space-y-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                      <CheckCircle2 className="h-4 w-4" /> Connection OK
                    </div>
                    <div className="font-mono">user: {testSsh.data.user}</div>
                    <div className="font-mono">hostname: {testSsh.data.hostname}</div>
                    <div className="font-mono">uname: {testSsh.data.uname}</div>
                  </div>
                ) : (
                  <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-600 dark:text-red-400">
                    <div className="flex items-center gap-2 font-medium"><AlertCircle className="h-4 w-4" /> Connection failed</div>
                    <div className="mt-1">{testSsh.data.error}</div>
                  </div>
                )
              )}
              {createDevice.isError && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-600 dark:text-red-400">
                  <div className="flex items-center gap-2 font-medium"><AlertCircle className="h-4 w-4" /> Couldn't create the device row</div>
                  <div className="mt-1">
                    The SSH test passed but persisting the device failed. Server said:
                  </div>
                  <div className="mt-1 font-mono text-[11px] break-words">
                    {extractApiErrorDetail(createDevice.error)}
                  </div>
                  <div className="mt-1">Hit Back to change the inputs and try again.</div>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStep(KIND_HAS_PREINSTALLED_KEY[kind] ? "reach" : "key")}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Back
                </button>
                {testSsh.data?.ok && KIND_HAS_FILEBEAT_INSTALL[kind] && (
                  <button type="button" onClick={() => setStep("provision")} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                    Provision now →
                  </button>
                )}
                {testSsh.data?.ok && !KIND_HAS_FILEBEAT_INSTALL[kind] && (
                  <button type="button" onClick={() => setStep("done")} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                    Finish →
                  </button>
                )}
              </div>
            </div>
          )}

          {step === "done" && device && (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-700 dark:text-emerald-400">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="h-4 w-4" /> Device created
                </div>
                <div className="mt-1">
                  {KIND_HAS_SSH[kind] && !KIND_HAS_FILEBEAT_INSTALL[kind] ? (
                    <>
                      <code className="font-mono">{device.name}</code> is in your fleet
                      with a verified <code className="font-mono">ssh</code> connector.
                      Add more connectors (shell_command / log_tail / syslog / etc.)
                      from the device page.
                    </>
                  ) : (
                    <>
                      <code className="font-mono">{device.name}</code> is now in your fleet.
                      Connectors are added from the device page — open it from the Devices list.
                    </>
                  )}
                </div>
                {createConnector.isError && (
                  <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-400">
                    Heads up: the SSH probe succeeded, but auto-creating the SSH connector
                    failed afterwards. Open the device page and add it manually — the
                    underlying path is verified working.
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="text-xs font-medium text-foreground">Suggested next steps</div>
                <NextStepsForKind kind={kind} />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button" onClick={close}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === "provision" && (
            <div className="space-y-3">
              <div className="text-sm font-medium">Install the agent</div>
              <div className="text-xs text-muted-foreground">
                Tyrael installs filebeat, configures it to ship logs to Graylog with your tenant tag, and starts the service. Takes 1-2 minutes.
              </div>
              <button type="button" onClick={startProvisioning} disabled={provisionEvents.length > 0 && !provisionDone} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {provisionEvents.length === 0 ? "Start provisioning" : provisionDone ? "Re-run" : "Running…"}
              </button>

              {provisionEvents.length > 0 && (
                <div className="mt-2 space-y-1.5 rounded-md border border-border bg-muted/30 p-3">
                  {provisionEvents.map((ev, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {ev.ok === true   && <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />}
                      {ev.ok === false  && <AlertCircle  className="mt-0.5 h-4 w-4 text-red-600" />}
                      {ev.ok === null   && <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />}
                      <div>
                        <span className="font-mono text-[10px] text-muted-foreground">{ev.step}</span>
                        <span className="ml-2">{ev.detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {provisionDone && (
                <div className={cn(
                  "rounded-md border p-3 text-xs",
                  provisionDone.status === "active"
                    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
                    : "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400",
                )}>
                  {provisionDone.status === "active"
                    ? "✓ Provisioning complete. Logs will appear in Graylog within ~30 seconds."
                    : `✗ Provisioning failed: ${provisionDone.last_error}`}
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep("test")} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
                {provisionDone?.status === "active" && (
                  <button type="button" onClick={close} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                    Done
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Per-kind next steps for the Done panel. Only kinds the wizard can
// reach without provisioning need entries here — linux/cpanel land in
// Done via the provision-success path which has its own copy.
function NextStepsForKind({ kind }: { kind: DeviceKind }) {
  switch (kind) {
    case "hikvision":
      return (
        <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
          <li>
            Add an <code className="font-mono">http_digest</code> connector — point its{" "}
            <code className="font-mono">url</code> at <code className="font-mono">http://&lt;ip&gt;:&lt;port&gt;</code> and{" "}
            <code className="font-mono">username_ref</code> / <code className="font-mono">password_ref</code> at{" "}
            <code className="font-mono">tenant_secrets</code>. Verify it goes green — that's Tyrael's
            ISAPI handle.
          </li>
          <li>
            Add a <code className="font-mono">syslog</code> connector. Tyrael will write the syslog
            target onto the device over ISAPI; first message in Graylog usually within 1-5 min.
          </li>
          <li>
            (Optional) Add an <code className="font-mono">http_poll</code> connector for periodic
            ISAPI snapshots (admin user list, recent access events, firmware version).
          </li>
        </ol>
      );
    case "mikrotik":
      return (
        <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
          <li>
            Add an <code className="font-mono">ssh</code> connector — the wizard's RouterOS-aware
            install panel will walk you through <code className="font-mono">/user ssh-keys import</code>.
          </li>
          <li>
            Add a <code className="font-mono">netflow</code> connector once{" "}
            <code className="font-mono">/ip traffic-flow</code> on the router is pointed at this VM's collector.
          </li>
        </ol>
      );
    case "windows":
    case "other":
      return (
        <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
          <li>
            Add an <code className="font-mono">ssh</code> connector if SSH is reachable — the wizard
            shows the install command and verifies the tenant key works.
          </li>
          <li>
            Add a <code className="font-mono">syslog</code> connector and configure the device to ship
            to <code className="font-mono">siem.nws.ro:514/UDP</code>; verify when messages flow.
          </li>
          <li>
            Add <code className="font-mono">shell_command</code> / <code className="font-mono">http_poll</code>{" "}
            connectors for any custom checks Tyrael should run periodically.
          </li>
        </ol>
      );
    case "unifi_ap":
    case "unifi_switch":
      return (
        <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
          <li>
            SSH already works — no manual key install needed. The tenant public key is pushed
            to every adopted device by the UniFi controller, and we just verified the
            two-hop path lands on this device.
          </li>
          <li>
            Add an <code className="font-mono">ssh</code> connector to mark this as the canonical
            access path. Tyrael will use it for ad-hoc commands during audits + IR.
          </li>
          <li>
            Add a <code className="font-mono">shell_command</code> connector pointing at{" "}
            <code className="font-mono">mca-dump</code> (APs) or <code className="font-mono">swctrl mac</code>{" "}
            (switches) for periodic state pulls — Tyrael archives the output for trend / drift detection.
          </li>
          <li>
            Add a <code className="font-mono">log_tail</code> connector on{" "}
            <code className="font-mono">/var/log/messages</code> to archive wireless events for later analysis.
          </li>
        </ol>
      );
    default:
      // linux/cpanel hit Done via the provision panel; this branch is
      // unreachable but kept exhaustive so a future kind addition fails
      // typecheck rather than silently rendering nothing.
      return null;
  }
}

// Pull the actual FastAPI `detail` string out of an ApiError so the
// wizard can render the real server reason ("a device with that name
// already exists", "proxy_jump_device_id ... not in your tenant",
// "multi-hop proxy chain forbidden", etc.) instead of a hardcoded guess.
// ApiError stores the response body as a raw string; we JSON-parse it
// best-effort and fall back to message on failure.
function extractApiErrorDetail(err: unknown): string {
  if (err == null) return "unknown error";
  // ApiError instance: try body string → JSON → .detail.
  const maybeApi = err as { status?: number; body?: unknown; message?: string };
  if (typeof maybeApi.body === "string") {
    try {
      const parsed = JSON.parse(maybeApi.body) as { detail?: unknown };
      if (typeof parsed.detail === "string" && parsed.detail.length > 0) {
        return parsed.detail;
      }
    } catch { /* body wasn't JSON — fall through to message */ }
  }
  return maybeApi.message ?? String(err);
}

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium">{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function StepBadge({ current }: { current: Step }) {
  const labels: Record<Step, string> = {
    kind:      "1. Kind",
    details:   "2. Details",
    reach:     "3. Reachability",
    key:       "4. Authorize key",
    test:      "5. Test",
    provision: "6. Install",
    done:      "Done",
  };
  return <span>{labels[current]}</span>;
}
